import React, { Component } from 'react';
import { connect } from 'react-redux';
import { matchPath } from 'react-router-dom';
import find from 'lodash/find';
import SlatePane from 'components/SlatePane/SlatePane';
import { updateSlatePane, openSlateInPane, closeSlatePane } from 'store/actions/slatePane';
import routes from 'config/routes';

function findRouteByPathname(pathname) {
  return find(routes, function (r) {
    return matchPath(pathname, { path: r.path, exact: r.exact });
  });
}

function doesPathnameShowSlatePane(pathname) {
  var route = findRouteByPathname(pathname);
  return route && route.showSlatePane;
}

function doesPathnameHaveSlatePaneNav(pathname) {
  var route = findRouteByPathname(pathname);
  return route && route.slatePaneNav;
}

function getPaneTabByPathname(pathname) {
  var route = findRouteByPathname(pathname);
  return route && route.slatePaneTab ? route.slatePaneTab : null;
}

export class SlatePaneManager extends Component {
  componentDidMount() {
    this.syncPagesClasses();
    this.updateCSSOffset();
  }

  componentDidUpdate(prevProps) {
    var isPaneDocked = this.props.isPaneDocked;
    var routeSupportsPane = doesPathnameShowSlatePane(this.props.locationPathname);
    var prevRouteSupportsPane = doesPathnameShowSlatePane(prevProps.locationPathname);

    this.syncPagesClasses();
    this.updateCSSOffset();

    // Reset container scroll when leaving a pane route.
    // The pane extends the container's scrollable area while visible;
    // the stale scrollLeft persists after the pane is hidden on non-pane routes.
    if (prevRouteSupportsPane && !routeSupportsPane) {
      var pagesEl = document.getElementById('pages');
      if (pagesEl) {
        var containerEl = pagesEl.querySelector('.container');
        if (containerEl) {
          containerEl.scrollLeft = 0;
        }
      }
    }

    // Infer docked tab when pane is docked with a slate but no tabs recorded.
    // This covers external opens (SlateView, SearchResult, CustomModule) that
    // dispatch updateSlatePane directly without setting slatePaneDockedTabs.
    var dockedTabs = this.props.slatePaneDockedTabs;
    if (isPaneDocked && (this.props.slateInSlatePaneUUID || this.props.contentJson) && dockedTabs.length === 0) {
      var inferredTab = getPaneTabByPathname(this.props.locationPathname);
      if (inferredTab) {
        this.props.updateSlatePane({
          slatePaneDockedTabs: [inferredTab],
        });
      }
    }

    // Auto-unpin / restore on route change
    if (this.props.locationPathname !== prevProps.locationPathname) {
      var newTab = getPaneTabByPathname(this.props.locationPathname);
      var prevTab = getPaneTabByPathname(prevProps.locationPathname);

      if (dockedTabs.length > 0 && prevTab !== newTab) {
        if (isPaneDocked && dockedTabs.indexOf(newTab) === -1) {
          // Leaving to a tab that was never docked -- collapse to hidden
          this.props.updateSlatePane({
            isPaneOpen: false,
            isPaneHidden: newTab ? true : false,
            isPaneDocked: false,
          });
        } else if (!isPaneDocked && dockedTabs.indexOf(newTab) !== -1 && (this.props.slateInSlatePaneUUID || this.props.contentJson)) {
          // Returning to a previously docked tab -- restore docked state
          this.props.updateSlatePane({
            isPaneOpen: true,
            isPaneHidden: false,
            isPaneDocked: true,
          });
        }
      }
    }
  }

  render() {
    var props = this.props;
    var routeSupportsPane = doesPathnameShowSlatePane(props.locationPathname);

    var slateInSlatePaneUUID = props.slateInSlatePaneUUID;
    var isPaneOpen = routeSupportsPane ? props.isPaneOpen : false;
    var isPaneHidden = routeSupportsPane ? props.isPaneHidden : false;
    var isPaneDocked = routeSupportsPane ? props.isPaneDocked : false;
    var contentJson = props.contentJson;
    var slatePaneNavList = props.slatePaneNavList;
    var currentSlate = props.currentSlate;

    // Compute prev/next from nav list only on routes that support it
    var onPrev;
    var onNext;

    if (doesPathnameHaveSlatePaneNav(props.locationPathname)) {
      var currentIndex = slatePaneNavList.indexOf(slateInSlatePaneUUID);

      if (currentIndex > 0) {
        onPrev = this.handleNavigatePrev;
      }
      if (currentIndex >= 0 && currentIndex < slatePaneNavList.length - 1) {
        onNext = this.handleNavigateNext;
      }
    }

    return React.createElement(React.Fragment, null,
      routeSupportsPane && isPaneOpen && !isPaneDocked && React.createElement('div', {
        className: 'slate-pane-overlay',
        onClick: this.handleHideSlatePane,
      }),
      React.createElement(SlatePane, {
        slateUUID: contentJson ? null : slateInSlatePaneUUID,
        imageUrl: currentSlate && currentSlate.imageUrl ? currentSlate.imageUrl : '',
        isOpen: isPaneOpen,
        isHidden: isPaneHidden,
        isDocked: (slateInSlatePaneUUID || contentJson) && isPaneDocked,
        contentJson: contentJson,
        contentMode: props.contentMode,
        slateType: props.slatePaneSlateType,
        onShow: this.handleShowSlatePane,
        onHide: this.handleCloseSlatePane,
        onDock: this.toggleDockSlatePane,
        onPrev: onPrev,
        onNext: onNext,
      })
    );
  }

  syncPagesClasses = () => {
    var pagesEl = document.getElementById('pages');
    if (!pagesEl) return;
    var routeSupportsPane = doesPathnameShowSlatePane(this.props.locationPathname);
    var hasContent = this.props.slateInSlatePaneUUID || this.props.contentJson;
    var showOverlay = this.props.isPaneOpen && !this.props.isPaneDocked && routeSupportsPane;
    var showDocked = this.props.isPaneDocked && routeSupportsPane && hasContent;

    if (showOverlay) {
      pagesEl.classList.add('slate-pane-open');
    } else {
      pagesEl.classList.remove('slate-pane-open');
    }
    if (showDocked) {
      pagesEl.classList.add('slate-pane-docked');
    } else {
      pagesEl.classList.remove('slate-pane-docked');
    }
  };

  updateCSSOffset = () => {
    var pagesEl = document.getElementById('pages');
    if (!pagesEl) return;
    var routeSupportsPane = doesPathnameShowSlatePane(this.props.locationPathname);
    var isPaneDocked = this.props.isPaneDocked;
    var isPaneOpen = this.props.isPaneOpen;
    var hasContent = this.props.slateInSlatePaneUUID || this.props.contentJson;
    var showDocked = isPaneDocked && routeSupportsPane && hasContent;
    var showOverlay = isPaneOpen && !isPaneDocked && routeSupportsPane;

    var isPaneHidden = this.props.isPaneHidden;
    var showHiddenSliver = isPaneHidden && !isPaneOpen && routeSupportsPane && hasContent;

    pagesEl.style.setProperty('--slate-pane-docked', showDocked ? '410px' : '0px');
    pagesEl.style.setProperty('--slate-pane-overlay', showOverlay ? '62px' : '0px');
    pagesEl.style.setProperty('--slate-pane-collapsed', showHiddenSliver ? '62px' : '0px');
  };

  handleNavigatePrev = () => {
    var slatePaneNavList = this.props.slatePaneNavList;
    var slateInSlatePaneUUID = this.props.slateInSlatePaneUUID;
    var currentIndex = slatePaneNavList.indexOf(slateInSlatePaneUUID);
    if (currentIndex > 0) {
      this.openSlatePane(slatePaneNavList[currentIndex - 1], {
        navList: slatePaneNavList,
        slateType: this.props.slatePaneSlateType,
      });
    }
  };

  handleNavigateNext = () => {
    var slatePaneNavList = this.props.slatePaneNavList;
    var slateInSlatePaneUUID = this.props.slateInSlatePaneUUID;
    var currentIndex = slatePaneNavList.indexOf(slateInSlatePaneUUID);
    if (currentIndex >= 0 && currentIndex < slatePaneNavList.length - 1) {
      this.openSlatePane(slatePaneNavList[currentIndex + 1], {
        navList: slatePaneNavList,
        slateType: this.props.slatePaneSlateType,
      });
    }
  };

  openSlatePane = (slateUUID, opts) => {
    this.props.openSlateInPane(slateUUID, opts);
  };

  handleShowSlatePane = () => {
    this.props.updateSlatePane({
      isPaneOpen: true,
      isPaneHidden: false,
    });
  };

  handleHideSlatePane = () => {
    this.props.updateSlatePane({
      isPaneOpen: false,
      isPaneHidden: true,
    });
  };

  handleCloseSlatePane = () => {
    this.props.closeSlatePane();
  };

  toggleDockSlatePane = () => {
    if (this.props.isPaneDocked) {
      // Unpinning: pane detaches to the floating overlay; remember the
      // user's choice for this pane session
      var undockTab = getPaneTabByPathname(this.props.locationPathname);
      var remainingTabs = this.props.slatePaneDockedTabs.filter(function (g) {
        return g !== undockTab;
      });
      this.props.updateSlatePane({
        isPaneOpen: true,
        isPaneHidden: false,
        isPaneDocked: false,
        paneUnpinnedByUser: true,
        slatePaneDockedTabs: remainingTabs,
      });
    } else {
      // Docking: add the current tab to the docked set
      var currentTab = getPaneTabByPathname(this.props.locationPathname);
      var tabs = this.props.slatePaneDockedTabs.slice();
      if (currentTab && tabs.indexOf(currentTab) === -1) {
        tabs.push(currentTab);
      }
      this.props.updateSlatePane({
        isPaneOpen: true,
        isPaneHidden: false,
        isPaneDocked: true,
        paneUnpinnedByUser: false,
        slatePaneDockedTabs: tabs,
      });
    }
  };
}

var mapStateToProps = function (state) {
  return {
    isPaneOpen: state.slatePane.isPaneOpen,
    isPaneHidden: state.slatePane.isPaneHidden,
    isPaneDocked: state.slatePane.isPaneDocked,
    slateInSlatePaneUUID: state.slatePane.slateInSlatePaneUUID,
    contentJson: state.slatePane.contentJson,
    contentMode: state.slatePane.contentMode,
    slatePaneNavList: state.slatePane.slatePaneNavList,
    slatePaneSlateType: state.slatePane.slatePaneSlateType,
    currentSlate: state.slateViews.currentSlate,
    slatePaneDockedTabs: state.slatePane.slatePaneDockedTabs,
    locationPathname: state.router.location.pathname,
  };
};

var mapDispatchToProps = {
  updateSlatePane: updateSlatePane,
  openSlateInPane: openSlateInPane,
  closeSlatePane: closeSlatePane,
};

export default connect(mapStateToProps, mapDispatchToProps)(SlatePaneManager);
